/**
 * @file components/chat/ChatComposer.tsx
 * Barre de saisie type messagerie : trombone (joindre fichier) + dictée vocale
 * (7.3-07) + champ texte + envoi.
 * Drag & drop de fichier également supporté par le parent (zone du fil).
 *
 * CHAT-03 — Dual mode :
 *   - activeDossierId présent  → comportement dossier (onAttachFile, accept=pdf)
 *   - activeDossierId absent   → upload conversationnel (onUploadAttachment,
 *                                accept multi-types) + preview AttachmentPreview
 */
import React, { useRef, useState } from 'react';
import { Mic, Paperclip, SendHorizontal } from 'lucide-react';
import { useSpeechToText } from '../../hooks/useSpeechToText.ts';
import CommandPalette, { CommandPaletteRef } from './CommandPalette.tsx';
import AttachmentPreview from './AttachmentPreview.tsx';
import type { AttachmentUploadResult } from '../../hooks/useChatThread.ts';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PendingAttachment {
  id: string;
  filename: string;
  size: number;
  mimeType: string;
  previewUrl?: string;
}

// ── Constantes ────────────────────────────────────────────────────────────────

/**
 * Types acceptés en mode conversationnel (pas de dossier actif).
 * En mode dossier, seul application/pdf est autorisé (comportement existant).
 */
const CONVERSATIONAL_ACCEPT =
  'application/pdf,.pdf,image/png,image/jpeg,image/webp,text/plain,text/markdown,.md,.txt,.png,.jpg,.jpeg,.webp';

const DOSSIER_ACCEPT = 'application/pdf,.pdf';

// ── Composant ─────────────────────────────────────────────────────────────────

export default function ChatComposer({
  disabled = false,
  onSendText,
  onAttachFile,
  onUploadAttachment,
  activeDossierId = null,
  placeholder = 'Écrivez un message…',
}: {
  disabled?: boolean;
  onSendText: (text: string, attachmentId?: string | null) => void;
  onAttachFile: (file: File) => void;
  /** CHAT-03 : upload conversationnel (mode sans dossier actif). */
  onUploadAttachment?: (file: File) => Promise<AttachmentUploadResult>;
  /** Dossier actif transmis depuis le thread — détermine le mode d'upload. */
  activeDossierId?: string | null;
  placeholder?: string;
}): React.ReactElement {
  const [value, setValue] = useState('');
  const [showPalette, setShowPalette] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const paletteRef = useRef<CommandPaletteRef>(null);

  // ── 7.3-07 : dictée vocale (Web Speech API) ───────────────────────────────
  const stt = useSpeechToText('fr-FR');
  const dictationBaseRef = useRef('');

  // Complète le champ avec la transcription courante (transcript + interim).
  // ⚠ useEffect intentionnel ici : stt est un hook externe, on réagit à ses
  // émissions — pas un état dérivé calculable sans effet.
  React.useEffect(() => {
    if (!stt.listening && !stt.transcript && !stt.interim) return;
    setValue(`${dictationBaseRef.current}${stt.transcript}${stt.interim}`);
  }, [stt.listening, stt.transcript, stt.interim]);

  const toggleDictation = () => {
    if (stt.listening) {
      stt.stop();
      return;
    }
    dictationBaseRef.current = value.trim() ? `${value.replace(/\s+$/u, '')} ` : '';
    stt.reset();
    stt.start();
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const submit = () => {
    const trimmed = value.trim();
    // Autoriser l'envoi si texte OU pièce jointe (pas les deux requis)
    if (!trimmed && !pendingAttachment) return;
    onSendText(trimmed, pendingAttachment?.id ?? null);
    setValue('');
    // Libérer l'URL objet de prévisualisation
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment(null);
    setUploadError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showPalette && paletteRef.current) {
      const handled = paletteRef.current.handleKeyDown(e);
      if (handled) {
        e.preventDefault();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // ── Upload de fichier (dual mode) ─────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // permet de re-sélectionner le même fichier

    setUploadError(null);

    // Mode dossier : comportement existant inchangé
    if (activeDossierId) {
      onAttachFile(file);
      return;
    }

    // Mode conversationnel : upload vers POST /api/v1/chat/attachment
    if (!onUploadAttachment) return;
    setUploading(true);
    try {
      const result = await onUploadAttachment(file);
      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;
      setPendingAttachment({
        id: result.attachment_id,
        filename: result.filename,
        size: result.size,
        mimeType: result.mime_type,
        previewUrl,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const removePendingAttachment = () => {
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment(null);
    setUploadError(null);
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────

  const canSend = !disabled && (value.trim().length > 0 || pendingAttachment !== null);

  return (
    <div className="relative border-t border-border bg-card">
      {/* Palette de commandes / */}
      {showPalette && (
        <CommandPalette
          ref={paletteRef}
          query={value.slice(1)}
          onSelect={(command) => {
            setValue(command + ' ');
            setShowPalette(false);
          }}
          onClose={() => setShowPalette(false)}
        />
      )}

      {/* CHAT-03 : aperçu de la pièce jointe en attente d'envoi */}
      {(pendingAttachment || uploading) && !activeDossierId && (
        <div className="px-3 pt-2">
          <AttachmentPreview
            filename={pendingAttachment?.filename ?? '…'}
            size={pendingAttachment?.size ?? 0}
            mimeType={pendingAttachment?.mimeType ?? ''}
            previewUrl={pendingAttachment?.previewUrl}
            uploading={uploading}
            onRemove={removePendingAttachment}
          />
        </div>
      )}

      {/* Erreur upload */}
      {uploadError && (
        <p role="alert" className="px-4 pt-1.5 text-[11px] text-red-500">
          {uploadError}
        </p>
      )}

      {/* 7.3-07 : retour d'erreur dictée */}
      {stt.error && (
        <p role="status" className="px-4 pt-2 text-[11px] text-red-500">
          {stt.error}
        </p>
      )}

      <div className="flex items-end gap-2 px-3 py-2.5">
        <input
          ref={fileInputRef}
          type="file"
          // En mode dossier → PDF uniquement ; en mode conversationnel → multi-types
          accept={activeDossierId ? DOSSIER_ACCEPT : CONVERSATIONAL_ACCEPT}
          className="hidden"
          onChange={(e) => { void handleFileChange(e); }}
        />
        <button
          type="button"
          aria-label="Joindre un document"
          disabled={disabled || uploading}
          onClick={() => fileInputRef.current?.click()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
        >
          <Paperclip size={20} />
        </button>

        {/* 7.3-07 : bouton micro — masqué si la Web Speech API est absente */}
        {stt.supported && (
          <button
            type="button"
            aria-label={stt.listening ? 'Arrêter la dictée vocale' : 'Dicter un message'}
            aria-pressed={stt.listening}
            title={stt.listening ? 'Arrêter la dictée' : 'Dicter un message (fr-FR)'}
            disabled={disabled}
            onClick={toggleDictation}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50 ${
              stt.listening
                ? 'animate-pulse bg-red-500/15 text-red-500'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <Mic size={20} />
          </button>
        )}

        <textarea
          rows={1}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v);
            // showPalette = dérivé direct de la valeur, pas de useEffect nécessaire
            setShowPalette(v.startsWith('/') && !v.includes(' '));
          }}
          onKeyDown={handleKeyDown}
          placeholder={stt.listening ? 'Je vous écoute…' : placeholder}
          className="max-h-32 flex-1 resize-none rounded-2xl border border-border bg-background px-4 py-2.5 text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring disabled:opacity-50"
        />

        <button
          type="button"
          aria-label="Envoyer"
          disabled={!canSend}
          onClick={submit}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-40"
        >
          <SendHorizontal size={18} />
        </button>
      </div>
    </div>
  );
}
